/**
 * Thin fetch wrapper around `POST /v1/pricing/calculate`. Mirrors
 * `infrastructure/lib/api.ts`'s `request()` auth handling (dev-bypass
 * token, force-refresh-on-401 retry) rather than a simpler local pattern —
 * a pricing/money surface should not silently send an unauthenticated
 * request in dev-bypass mode, nor give up on an expired token without one
 * retry (caught in code review, see
 * `docs/features/pricing-engine-frontend/DEVELOPMENT_LOG.md`).
 *
 * This endpoint requires authentication (`05_API_SPECIFICATION.md`'s
 * "Calculate Price" entry — pricing may depend on user/partner context).
 * It is not implemented on the backend yet (`04_PRICING_ENGINE.md`). This
 * client is built against that document's approved contract, not an
 * invented one.
 */
import { API_BASE } from '@/src/infrastructure/lib/api';
import { auth } from '@/src/infrastructure/config/firebase';
import { logout } from '@/src/modules/auth/session';
import { PRODUCT_UNAVAILABLE_MESSAGE } from './copy';
import type { CalculatePriceRequest, PriceQuote } from './types';

/**
 * The Collection either doesn't exist or is no longer active — the backend
 * returns `422` for both (`05_API_SPECIFICATION.md`'s Calculate Price entry), matching the existing
 * `CatalogNotFoundError` precedent in `modules/catalog/api.ts` for the
 * identical "this product isn't purchasable right now" situation.
 */
export class ProductUnavailableForPricingError extends Error {
  constructor() {
    super(PRODUCT_UNAVAILABLE_MESSAGE);
    this.name = 'ProductUnavailableForPricingError';
  }
}

async function authPost<T>(url: string, body: unknown, retried = false): Promise<T> {
  const isBypass = import.meta.env.VITE_DEV_AUTH_BYPASS === 'true';
  const token = isBypass ? 'dev-token' : await auth.currentUser?.getIdToken(retried).catch(() => null);
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });

  if (res.status === 401 && !retried && !isBypass && auth.currentUser) {
    return authPost<T>(url, body, true);
  }
  if (res.status === 401) {
    if (isBypass) {
      throw new Error('Unauthorized: ensure backend is also running with DEV_AUTH_BYPASS=true');
    }
    await logout().catch(() => undefined);
    window.location.href = '/login';
    throw new Error('Session expired');
  }
  if (res.status === 422) {
    throw new ProductUnavailableForPricingError();
  }
  if (!res.ok) {
    // Deliberately not surfacing the response body's message — matches
    // `purchase/api.ts`'s `authFetch`. The caller (`usePricing`) never shows
    // a raw error string to the customer either; keeping this thrown Error
    // free of backend text means no future `catch (e) { setError(e.message) }`
    // can accidentally re-introduce the raw-error-leak bug fixed in code
    // review (see `docs/features/pricing-engine-frontend/DEVELOPMENT_LOG.md`).
    throw new Error(`HTTP ${res.status}`);
  }
  const responseBody = await res.json();
  return (responseBody?.data !== undefined ? responseBody.data : responseBody) as T;
}

/**
 * Preview-only price calculation — never binding, never frozen (architecture
 * §6). The backend re-runs this same calculation, itself, at Purchase
 * creation — this call's result is for display only.
 */
export function calculatePrice(request: CalculatePriceRequest): Promise<PriceQuote> {
  return authPost<PriceQuote>(`${API_BASE}/pricing/calculate`, request);
}
